// src/App.js
import React, { lazy, Suspense } from 'react';
import './css/modules/variables.css';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import { CartProvider } from './components/CartContext';
import GoogleTag from './components/GoogleTag';
import CookieConsent from './components/CookieConsent';
import { LICENCJA_PAGES } from './seo/siteMeta';
import PageViewTracker from './components/PageViewTracker';

/*
 * Każda trasa jedzie w osobnej paczce JS.
 *
 * Startowa paczka to tylko szkielet (layout, router, kontekst użytkownika),
 * a treść trasy dochodzi osobnym plikiem. Strony prerenderowane nie mrugają:
 * prerender dokleja im <link rel="preload" as="script"> z paczką ich trasy
 * (patrz src/lib/prerenderChunks.js), więc paczka jedzie równolegle z main.js.
 */
// Strona główna jedzie NIE-leniwie. React.lazy zawsze rozwiązuje się
// asynchronicznie, więc przy hydratacji Suspense na moment czyściłby
// prerenderowany hero i element LCP montowałby się od nowa. Pozostałe trasy
// zostają leniwe.
import Home from './components/WhyToBuyUs';

/* Mechanika testu przeniesiona z pistolet.org: pojedynek z przeciwnikiem,
   pasek energii, zegar na pytanie i ekrany wyniku. */
const IntroEnemy = lazy(() => import('./components/IntroEnemy'));
const Quiz = lazy(() => import('./components/QuizCyberpunkSingle'));
const CongratsScreen = lazy(() => import('./components/CongratsScreen'));
const DefeatScreen = lazy(() => import('./components/DefeatScreen'));
const TieScreen = lazy(() => import('./components/TieScreen'));
const Rating = lazy(() => import('./components/Rating'));
const Shop = lazy(() => import('./components/Shop'));

/* Tryb nauki i podstrony treściowe - to one łapią ruch z wyszukiwarki. */
const StudyHub = lazy(() => import('./components/StudyHub'));
const StudyMode = lazy(() => import('./components/StudyMode'));
const QuestionPage = lazy(() => import('./components/QuestionPage'));
const LicencjaPage = lazy(() => import('./components/LicencjaPage'));
const EgzaminULC = lazy(() => import('./components/EgzaminULC'));

const Login = lazy(() => import('./components/Login'));
const Regulamin = lazy(() => import('./components/Regulamin'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const ThankYou = lazy(() => import('./components/ThankYou'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const PricingOptions = lazy(() => import('./components/PricingOptions'));
const PurchaseRegistration = lazy(() => import('./components/PurchaseRegistration'));

function App() {
  return (
    <CartProvider>
      <GoogleTag />
      <PageViewTracker />
      <Layout>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />

            {/* Strony filarowe licencji - jeden komponent, treść w src/data/licencje.js */}
            {LICENCJA_PAGES.map(l => (
              <Route key={l.path} path={l.path} element={<LicencjaPage slug={l.slug} />} />
            ))}
            <Route path="/egzamin-teoretyczny-ulc" element={<EgzaminULC />} />

            {/* Test: pojedynek z przeciwnikiem (mechanika z pistolet.org) */}
            <Route path="/intro" element={<IntroEnemy />} />
            <Route path="/quiz/:enemySlug" element={<Quiz />} />
            <Route path="/defeated" element={<DefeatScreen />} />
            <Route path="/ranking" element={<Rating />} />
            <Route path="/shop" element={<Shop />} />

            {/* Tryb nauki po przedmiotach */}
            <Route path="/nauka/:licencja" element={<StudyHub />} />
            <Route path="/nauka/:licencja/:przedmiot" element={<StudyMode />} />

            {/* Podstrona każdego pytania z omówieniem - ruch long-tail */}
            <Route path="/pytania/:slug" element={<QuestionPage />} />

            <Route path="/cennik" element={<PricingOptions />} />
            <Route path="/o-nas" element={<AboutUs />} />
            <Route path="/kontakt" element={<ContactForm />} />
            <Route path="/regulamin" element={<Regulamin />} />
            <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/zakup" element={<PurchaseRegistration />} />
            <Route path="/podziekowanie" element={<ThankYou />} />

            {/* Ekrany wyniku pojedynku - trasy dwuczłonowe muszą stać na końcu,
                inaczej ":enemySlug" przechwyciłby adresy statyczne wyżej. */}
            <Route path="/:enemySlug/success" element={<CongratsScreen />} />
            <Route path="/:enemySlug/tie" element={<TieScreen />} />
          </Routes>
        </Suspense>
        <CookieConsent />
      </Layout>
    </CartProvider>
  );
}

export default App;
